import React, { useContext, useState } from "react";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import { Box, Button, IconButton, LinearProgress, Typography, styled } from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { ContainerModal } from "../../pages/MyTracks/styles";
import { styles } from "../../pages/MyTracks/styles";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { UserDetailsContext, UserDetailsContextInterface } from "../../contexts";
import { postTrack } from "../../services/trackService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { TrackObj } from "../../pages/MyTracks/types";
import Autocomplete, { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { GenresArray, MoodsArray } from "../constants";
import { autoCompleteStlye, useStyles } from "../../pages/MyTracks/styles";
import { TEXT_COLOR, FONT_FAMILY, APP_BACKGROUND_COLOR } from "../../theme";
import { Spinner } from "react-bootstrap";

interface BatchEditModalProps {
  isModalOpen: boolean;
  checkedTracks: TrackObj[];
  handleClose: () => void;
  handleRefresh: () => void;
}

const BATCH_EDIT_VALIDATION_SCHEMA = Yup.object().shape({
  artist: Yup.string().max(120, "Artist name must be at most 120 characters"),
  album: Yup.string().max(120, "Album name must be at most 120 characters"),
});

const initialValues = {
  artist: "",
  album: "",
};

const BatchEditModalFormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: APP_BACKGROUND_COLOR,
  color: TEXT_COLOR,
  width: "40%",
  [theme.breakpoints.down("md")]: {
    width: "80%",
  },
}));

const BatchEditModal: React.FC<BatchEditModalProps> = ({
  isModalOpen,
  checkedTracks,
  handleClose,
  handleRefresh,
}) => {
  const { getValidAccessToken } = useGetValidAccessToken();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { enterprise, isEnterpriseAccount } = useContext<UserDetailsContextInterface>(UserDetailsContext);
  const classes = useStyles();

  const [genres, setGenres] = useState<string[]>([]);
  const [moods, setMoods] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);

  const onSubmit = async (
    values: typeof initialValues,
    { setSubmitting, resetForm }: any
  ) => {
    try {
      setIsUpdating(true);
      setUpdateProgress(0);
      const token = await getValidAccessToken();

      let successCount = 0;
      let failedCount = 0;
      const failedTracks: Array<{ trackName: string; trackId: string; reason: string }> = [];

      console.log("[BATCH EDIT] Starting batch edit for", checkedTracks.length, "tracks");
      console.log("[BATCH EDIT] Updates:", {
        artist: values.artist || "(no change)",
        album: values.album || "(no change)",
        genres: genres.length > 0 ? genres : "(no change)",
        moods: moods.length > 0 ? moods : "(no change)",
      });

      // Update each selected track
      for (let i = 0; i < checkedTracks.length; i++) {
        const trackObj = checkedTracks[i];
        const track = trackObj.track;

        if (!track) {
          const reason = "Track object is null/undefined";
          console.error(`[BATCH EDIT] [${i + 1}/${checkedTracks.length}] FAILED: ${reason}`);
          failedTracks.push({
            trackName: "Unknown Track",
            trackId: "N/A",
            reason: reason,
          });
          failedCount++;
          continue;
        }

        const trackId = track.getId();
        const trackName = track.getTitle() || "Untitled";
        const currentArtist = track.getArtist() || "Unknown Artist";
        const projectId = track.getProjectId() || "(no project)";

        console.log(`\n[BATCH EDIT] [${i + 1}/${checkedTracks.length}] Updating: "${trackName}" by ${currentArtist}`);
        console.log(`[BATCH EDIT]    Track ID: ${trackId}`);
        console.log(`[BATCH EDIT]    Project ID: ${projectId}`);

        try {
          // Update artist if provided
          if (values.artist.trim()) {
            console.log(`[BATCH EDIT]    Artist: "${currentArtist}" -> "${values.artist.trim()}"`);
            track.setArtist(values.artist.trim());
          }

          // Update album if provided
          if (values.album.trim()) {
            const songMetadata = track.getSongMetadata();
            if (songMetadata) {
              const currentAlbum = songMetadata.getAlbum() || "(none)";
              console.log(`[BATCH EDIT]    Album: "${currentAlbum}" -> "${values.album.trim()}"`);
              songMetadata.setAlbum(values.album.trim());
              track.setSongMetadata(songMetadata);
            }
          }

          // Update genres if provided
          if (genres.length > 0) {
            const songMetadata = track.getSongMetadata();
            if (songMetadata) {
              const currentGenres = songMetadata.getGenreList() || [];
              console.log(`[BATCH EDIT]    Genres: [${currentGenres.join(", ")}] -> [${genres.join(", ")}]`);
              songMetadata.setGenreList(genres);
              track.setSongMetadata(songMetadata);
            }
          }

          // Update moods if provided
          if (moods.length > 0) {
            const currentMoods = track.getMoodsList() || [];
            console.log(`[BATCH EDIT]    Moods: [${currentMoods.join(", ")}] -> [${moods.join(", ")}]`);
            console.log(`[BATCH EDIT]    Track has project: ${track.getProjectId() ? "YES" : "NO (this may cause mood inheritance error)"}`);
            track.setMoodsList(moods);
          }

          // Save the track
          console.log(`[BATCH EDIT]    Saving to server...`);
          const response = await postTrack(track, token, userId);

          if (response) {
            console.log(`[BATCH EDIT]    SUCCESS: "${trackName}" updated successfully`);
            successCount++;
          } else {
            const reason = "API returned null/empty response";
            console.error(`[BATCH EDIT]    FAILED: ${reason}`);
            failedTracks.push({
              trackName: trackName,
              trackId: trackId,
              reason: reason,
            });
            failedCount++;
          }
        } catch (error: any) {
          const reason = error?.message || error?.code || String(error);
          console.error(`[BATCH EDIT]    FAILED: "${trackName}" (ID: ${trackId})`);
          console.error(`[BATCH EDIT]    Error:`, error);
          console.error(`[BATCH EDIT]    Reason:`, reason);
          failedTracks.push({
            trackName: trackName,
            trackId: trackId,
            reason: reason,
          });
          failedCount++;
        }

        // Update progress
        setUpdateProgress(Math.round(((i + 1) / checkedTracks.length) * 100));
      }

      setIsUpdating(false);
      setSubmitting(false);

      // Print final summary
      console.log("\n" + "=".repeat(60));
      console.log("BATCH EDIT SUMMARY");
      console.log("=".repeat(60));
      console.log(`Successful: ${successCount} tracks`);
      console.log(`Failed: ${failedCount} tracks`);
      console.log(`Success Rate: ${Math.round((successCount / checkedTracks.length) * 100)}%`);

      if (failedTracks.length > 0) {
        console.log("\nFAILED TRACKS:");
        failedTracks.forEach((fail, idx) => {
          console.log(`\n${idx + 1}. Track: "${fail.trackName}"`);
          console.log(`   ID: ${fail.trackId}`);
          console.log(`   Reason: ${fail.reason}`);
        });
      }
      console.log("=".repeat(60) + "\n");

      // Show success/error message
      if (successCount > 0 && failedCount === 0) {
        toast.success(`Successfully updated ${successCount} track${successCount > 1 ? 's' : ''}!`);
      } else if (successCount > 0 && failedCount > 0) {
        toast.warning(`Updated ${successCount} track${successCount > 1 ? 's' : ''}, ${failedCount} failed. Check console for details.`);
      } else {
        toast.error("Failed to update tracks. Check console for details.");
      }

      // Reset form and refresh
      resetForm();
      setGenres([]);
      setMoods([]);
      handleRefresh();
      handleClose();
    } catch (error) {
      console.error("[BATCH EDIT] CRITICAL ERROR:", error);
      toast.error("An error occurred while updating tracks");
      setIsUpdating(false);
      setSubmitting(false);
    }
  };

  return (
    <ContainerModal
      open={isModalOpen}
      onClose={handleClose}
      style={{ overflow: "auto" }}
    >
      <Box sx={styles.uploadTracksContainer}>
        <BatchEditModalFormContainer>
          <Box sx={{ padding: "15px" }}>
            <Box style={styles.modalHeaderContainer}>
              <span style={styles.modalHeader}>
                <Box sx={styles.headerIconContainer}>
                  <EditIcon sx={{ color: "white" }} />
                </Box>
                Edit {checkedTracks.length} Track{checkedTracks.length > 1 ? 's' : ''}
              </span>
              <IconButton onClick={handleClose} disabled={isUpdating}>
                <CloseIcon />
              </IconButton>
            </Box>
            <hr style={{ marginBottom: 30 }} />

            <Formik
              initialValues={initialValues}
              onSubmit={onSubmit}
              validationSchema={BATCH_EDIT_VALIDATION_SCHEMA}
            >
              {({
                values,
                handleChange,
                handleBlur,
                isSubmitting,
                errors,
                touched,
                setFieldValue,
              }) => (
                <FormikForm>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        marginBottom: 3,
                        color: TEXT_COLOR,
                        opacity: 0.7
                      }}
                    >
                      Only fill in the fields you want to update. Empty fields will be ignored.
                    </Typography>

                    {/* Artist Field */}
                    <Box className="input" sx={{ marginBottom: 2 }}>
                      <label style={{ color: TEXT_COLOR }} htmlFor="artist">
                        Artist <span className="optional-input">(optional)</span>
                      </label>
                      <input
                        style={{
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: `1px solid ${errors.artist && touched.artist ? 'red' : 'gray'}`,
                          backgroundColor: "transparent",
                          color: TEXT_COLOR,
                          fontFamily: FONT_FAMILY,
                        }}
                        type="text"
                        name="artist"
                        id="artist"
                        placeholder="Enter new artist name"
                        value={values.artist}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting || isUpdating}
                      />
                      {errors.artist && touched.artist && (
                        <Typography variant="caption" color="error">
                          {errors.artist}
                        </Typography>
                      )}
                    </Box>

                    {/* Album Field */}
                    <Box className="input" sx={{ marginBottom: 2 }}>
                      <label style={{ color: TEXT_COLOR }} htmlFor="album">
                        Album <span className="optional-input">(optional)</span>
                      </label>
                      <input
                        style={{
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: `1px solid ${errors.album && touched.album ? 'red' : 'gray'}`,
                          backgroundColor: "transparent",
                          color: TEXT_COLOR,
                          fontFamily: FONT_FAMILY,
                        }}
                        type="text"
                        name="album"
                        id="album"
                        placeholder="Enter new album name"
                        value={values.album}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting || isUpdating}
                      />
                      {errors.album && touched.album && (
                        <Typography variant="caption" color="error">
                          {errors.album}
                        </Typography>
                      )}
                    </Box>

                    {/* Genre Field */}
                    <Box sx={{ marginBottom: 2 }}>
                      <label htmlFor="genres" style={{ color: TEXT_COLOR }}>
                        Genre <span className="optional-input">(optional)</span>
                      </label>
                      <Stack spacing={3}>
                        <Autocomplete
                          disabled={isSubmitting || isUpdating}
                          autoComplete
                          value={genres || []}
                          sx={autoCompleteStlye}
                          options={GenresArray}
                          multiple={true}
                          onChange={(
                            event: React.SyntheticEvent<Element, Event>,
                            value: string[] | null,
                            _reason: AutocompleteChangeReason
                          ) => {
                            if (value != null) {
                              setGenres(value);
                              setFieldValue("genres", value);
                            } else {
                              setGenres([]);
                              setFieldValue("genres", []);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label=" "
                              InputLabelProps={{
                                shrink: false,
                              }}
                              classes={{ root: classes.customTextField }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                              {option}
                            </li>
                          )}
                        />
                      </Stack>
                    </Box>

                    {/* Mood Field */}
                    <Box sx={{ marginBottom: 3 }}>
                      <label htmlFor="moods" style={{ color: TEXT_COLOR }}>
                        Mood <span className="optional-input">(optional)</span>
                      </label>
                      <Stack spacing={3}>
                        <Autocomplete
                          disabled={isSubmitting || isUpdating}
                          autoComplete
                          multiple
                          value={moods || []}
                          sx={autoCompleteStlye}
                          options={MoodsArray}
                          onChange={(
                            event: React.SyntheticEvent<Element, Event>,
                            value: string[] | null,
                            _reason: AutocompleteChangeReason
                          ) => {
                            if (value != null) {
                              setMoods(value);
                              setFieldValue("moods", value);
                            } else {
                              setMoods([]);
                              setFieldValue("moods", []);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label=" "
                              InputLabelProps={{
                                shrink: false,
                              }}
                              classes={{ root: classes.customTextField }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                              {option}
                            </li>
                          )}
                        />
                      </Stack>
                    </Box>

                    {/* Progress Bar */}
                    {isUpdating && (
                      <Box sx={{ marginBottom: 2 }}>
                        <Typography variant="body2" sx={{ marginBottom: 1, color: TEXT_COLOR }}>
                          Updating tracks... {updateProgress}%
                        </Typography>
                        <LinearProgress variant="determinate" value={updateProgress} />
                      </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        onClick={handleClose}
                        disabled={isSubmitting || isUpdating}
                        sx={{
                          ...styles.skipButton,
                          ...{ marginRight: "25px" },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || isUpdating}
                        fullWidth
                        sx={styles.modalSubmitButton}
                        size="small"
                        variant="contained"
                      >
                        {isSubmitting || isUpdating ? (
                          <>
                            <Spinner
                              as="span"
                              animation="grow"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            <span style={{ marginLeft: "10px" }}>
                              Updating...
                            </span>
                          </>
                        ) : (
                          "Update Tracks"
                        )}
                      </Button>
                    </Box>
                  </Box>
                </FormikForm>
              )}
            </Formik>
          </Box>
        </BatchEditModalFormContainer>
      </Box>
    </ContainerModal>
  );
};

export default BatchEditModal;
